import React, { useState, useCallback } from 'react';
import { View, StyleSheet, Text } from 'react-native';

import { Button, TextInput, HelperText } from '@/components/ui';
import { spacing } from '@/styles';
import type { MaterialCost } from '@/types/models';

export type MaterialCostFormData = {
	name: string;
	description: string;
	defaultCost: string;
	unitType: string;
};

type MaterialCostFormProps = {
	initialData?: MaterialCost;
	onSave: (data: MaterialCostFormData) => Promise<void>;
	onCancel?: () => void;
	disabled?: boolean;
};

export const MaterialCostForm: React.FC<MaterialCostFormProps> = ({
	initialData,
	onSave,
	onCancel,
	disabled,
}) => {
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Form state
	const [name, setName] = useState(initialData?.name || '');
	const [description, setDescription] = useState(initialData?.description || '');
	const [defaultCost, setDefaultCost] = useState(
		initialData?.defaultCost !== undefined ? initialData.defaultCost.toString() : '',
	);
	const [unitType, setUnitType] = useState(initialData?.unitType || '');

	// Validate the form
	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!name.trim()) {
			newErrors.name = 'Name is required';
		}

		if (!defaultCost.trim()) {
			newErrors.defaultCost = 'Default cost is required';
		} else {
			const cost = parseFloat(defaultCost);
			if (isNaN(cost) || cost < 0) {
				newErrors.defaultCost = 'Default cost must be a non-negative number';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form submission
	const handleSubmit = useCallback(async () => {
		if (!validate()) return;

		setLoading(true);
		try {
			await onSave({
				name: name.trim(),
				description: description.trim(),
				defaultCost: defaultCost.trim(),
				unitType: unitType.trim(),
			});
		} catch (error) {
			console.error('Error saving material cost:', error);
		} finally {
			setLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- validate is inline function; adding it would cause re-creation every render
	}, [name, description, defaultCost, unitType, onSave]);

	return (
		<View style={styles.formContainer}>
			<TextInput
				label="Name"
				value={name}
				onChangeText={setName}
				error={!!errors.name}
				style={styles.input}
				disabled={loading}
			/>
			{errors.name && <HelperText type="error">{errors.name}</HelperText>}

			<TextInput
				label="Description (Optional)"
				value={description}
				onChangeText={setDescription}
				style={styles.input}
				multiline
				disabled={loading}
			/>

			<TextInput
				label="Default Cost ($)"
				value={defaultCost}
				onChangeText={setDefaultCost}
				keyboardType="decimal-pad"
				error={!!errors.defaultCost}
				style={styles.input}
				disabled={loading}
			/>
			{errors.defaultCost && (
				<HelperText type="error">{errors.defaultCost}</HelperText>
			)}

			<TextInput
				label="Unit Type (Optional)"
				value={unitType}
				onChangeText={setUnitType}
				placeholder="e.g., each, box, pound"
				style={styles.input}
				disabled={loading}
			/>

			<View style={styles.buttonContainer}>
				{onCancel && (
					<Button
						onPress={onCancel}
						style={{ marginRight: spacing.m }}
						disabled={loading || disabled}
					>
						<Text>Cancel</Text>
					</Button>
				)}
				<Button
					mode="contained"
					onPress={handleSubmit}
					loading={loading}
					disabled={loading || disabled}
				>
					{initialData ? 'Update' : 'Create'}
				</Button>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	formContainer: {
		padding: spacing.m,
	},
	input: {
		marginBottom: spacing.s,
	},
	buttonContainer: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		marginTop: spacing.m,
	},
});
